import { lighten } from '@mui/material';
import Chart from 'react-google-charts';
import { Colors } from 'src/shared/components/styles/colors';
import { useCallback, useMemo } from 'react';
import { GenderKeyEnum } from 'src/shared/models/enums/genderKey.enum';
import { useTranslation } from 'react-i18next';
import { AgeSpan } from '../models/ageAndGenderDistribution';

type StatisticsChartPieProps = {
  ageSpans: AgeSpan[];
};

export const StatisticsChartPie = (props: StatisticsChartPieProps) => {
  const { ageSpans } = props;
  const { t } = useTranslation();

  const labelize = useCallback(
    (name?: string) => {
      const v = (name ?? '').trim();
      return v === GenderKeyEnum.Unknown ? t('statistics:ageAndGenderDistribution.unknownAge') : v;
    },
    [t],
  );

  // 1) Bygg chartData med OKÄND först
  const chartData = useMemo(() => {
    const nonZero = (ageSpans ?? []).filter((s) => (s?.count ?? 0) > 0);

    const isUnknown = (n?: string) => n === GenderKeyEnum.Unknown;
    const unknown = nonZero.find((s) => isUnknown(s.name));
    const rest = nonZero.filter((s) => !isUnknown(s.name));

    const rows: (string | number)[][] = [
      ...(unknown ? [[labelize(unknown.name), Number(unknown.count ?? 0)]] : []), // <- "okänd ålder" först
      ...rest.map((s) => [labelize(s.name), Number(s.count ?? 0)]),
    ];

    return rows.length
      ? [['Age span', 'Quantity'], ...rows]
      : [
          ['Age span', 'Quantity'],
          ['—', 0],
        ];
  }, [ageSpans, labelize]);

  // 2) Färger: första (okänd) = grå
  const segmentCount = Math.max(0, chartData.length - 1);
  const colors = useMemo(() => {
    const base = [
      lighten(Colors.ChipColors.Blue, 0.5),
      lighten(Colors.ChipColors.Green, 0.5),
      lighten(Colors.ChipColors.Yellow, 0.5),
      lighten(Colors.ChipColors.Red, 0.5),
      Colors.ChipColors.DustyBlue,
    ];

    // Bygg palett för exakt antal segment
    const palette = Array.from({ length: segmentCount }, (_, i) => base[i % base.length]);

    // Om första etiketten är "Okänd ålder" -> gör första färgen grå
    const firstLabel = chartData[1]?.[0]; // efter headern
    const unknownLabel = t('statistics:ageAndGenderDistribution.unknownAge');
    if (segmentCount > 0 && firstLabel === unknownLabel) {
      palette[0] = '#9ca3af'; // valfri grå
    }

    return palette;
  }, [segmentCount, chartData, t]);

  return (
    <div className='legend'>
      <Chart
        width='100%'
        height='100%' // Set legend height to 100% to fill the container
        chartType='PieChart'
        loader={<div>Loading Chart</div>}
        data={chartData}
        options={{
          pieHole: 0.8,
          is3D: false,
          chartArea: { left: 0 }, // Adjust the chart area size
          animation: {
            startup: true,
            duration: 1000,
            easing: 'out',
          },
          legend: {
            position: 'right',
            alignment: 'start',
            textStyle: {
              fontSize: 14, // Adjust legend font size if needed
            },
            // Set the height of the legend here (adjust as needed)
            height: '400px',
          },
          pieSliceText: 'none',
          slices: {},
          colors,
        }}
        rootProps={{ 'data-testid': '2' }}
      />
    </div>
  );
};
